import React, { useState, useEffect } from 'react';
import AccountingService from '../../services/AccountingService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Book as BookIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

const JournalEntryRow = ({ entry }) => {
  const [open, setOpen] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {new Date(entry.entryDate).toLocaleDateString()}
        </TableCell>
        <TableCell>{entry.referenceNumber || '-'}</TableCell>
        <TableCell>{entry.description}</TableCell>
        <TableCell>{entry.sourceDocument || 'Manual'}</TableCell>
        <TableCell>
          {entry.postedByUser ? `${entry.postedByUser.firstName} ${entry.postedByUser.lastName}` : 'System'}
        </TableCell>
        <TableCell align="right">
          <Chip 
            label={formatCurrency(entry.journalEntryLines?.reduce((sum, line) => sum + line.debit, 0) || 0)} 
            color="default" 
            variant="outlined" 
            size="small"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div" size="small">
                Details
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entry.journalEntryLines && entry.journalEntryLines.map((line) => (
                    <TableRow key={line.journalEntryLineId}>
                      <TableCell>
                        {line.account ? `${line.account.accountCode} - ${line.account.accountName}` : 'Unknown Account'}
                      </TableCell>
                      <TableCell>{line.description}</TableCell>
                      <TableCell align="right">
                        {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(entry.journalEntryLines?.reduce((sum, line) => sum + line.debit, 0) || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(entry.journalEntryLines?.reduce((sum, line) => sum + line.credit, 0) || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const JournalEntries = () => {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    entryDate: new Date(),
    description: '',
    referenceNumber: '',
    lines: [
      { accountId: '', description: '', debit: 0, credit: 0 },
      { accountId: '', description: '', debit: 0, credit: 0 }
    ]
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, accountsRes] = await Promise.all([
        AccountingService.getAllJournalEntries(),
        AccountingService.getAllAccounts()
      ]);
      
      const entriesData = Array.isArray(entriesRes.data) ? entriesRes.data : 
                          (entriesRes.data && Array.isArray(entriesRes.data.data)) ? entriesRes.data.data : [];
      setEntries(entriesData);
      
      const accountsData = Array.isArray(accountsRes.data) ? accountsRes.data : 
                           (accountsRes.data && Array.isArray(accountsRes.data.data)) ? accountsRes.data.data : [];
      setAccounts(accountsData);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load journal entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setNewEntry({
      entryDate: new Date(),
      description: '',
      referenceNumber: '',
      lines: [
        { accountId: '', description: '', debit: 0, credit: 0 },
        { accountId: '', description: '', debit: 0, credit: 0 }
      ]
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEntryChange = (field, value) => {
    setNewEntry({
      ...newEntry,
      [field]: value
    });
  };

  const handleLineChange = (index, field, value) => {
    const updatedLines = [...newEntry.lines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };
    
    // If setting debit, clear credit and vice versa (usually)
    // But allow both to be 0
    if (field === 'debit' && value > 0) updatedLines[index].credit = 0;
    if (field === 'credit' && value > 0) updatedLines[index].debit = 0;
    
    setNewEntry({
      ...newEntry,
      lines: updatedLines
    });
  };

  const addLine = () => {
    setNewEntry({
      ...newEntry,
      lines: [...newEntry.lines, { accountId: '', description: '', debit: 0, credit: 0 }]
    });
  };

  const removeLine = (index) => {
    if (newEntry.lines.length <= 2) {
      showSnackbar('Journal entry must have at least 2 lines', 'warning');
      return;
    }
    const updatedLines = newEntry.lines.filter((_, i) => i !== index);
    setNewEntry({
      ...newEntry,
      lines: updatedLines
    });
  };

  const validateEntry = () => {
    if (!newEntry.description) return "Description is required";
    
    let totalDebit = 0;
    let totalCredit = 0;
    
    for (const line of newEntry.lines) {
      if (!line.accountId) return "All lines must have an account selected";
      totalDebit += Number(line.debit) || 0;
      totalCredit += Number(line.credit) || 0;
    }
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      return `Entry is not balanced. Total Debit: ${totalDebit}, Total Credit: ${totalCredit}`;
    }
    
    if (totalDebit === 0) return "Entry cannot be zero";
    
    return null;
  };

  const handleSaveEntry = async () => {
    const validationError = validateEntry();
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }
    
    try {
      const journalEntryPayload = {
        entryDate: newEntry.entryDate,
        description: newEntry.description,
        referenceNumber: newEntry.referenceNumber,
        sourceDocument: 'Manual',
        journalEntryLines: newEntry.lines.map(line => ({
          accountId: line.accountId,
          description: line.description || newEntry.description,
          debit: Number(line.debit) || 0,
          credit: Number(line.credit) || 0
        }))
      };
      
      await AccountingService.createJournalEntry(journalEntryPayload);
      showSnackbar('Journal entry created successfully', 'success');
      handleCloseDialog();
      fetchData();
    } catch (err) {
      console.error('Error creating journal entry:', err);
      showSnackbar(err.response?.data?.message || 'Failed to create journal entry', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <BookIcon sx={{ mr: 2 }} />
          Journal Entries
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            New Entry
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Date</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Posted By</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.length > 0 ? (
                    entries.map((entry) => (
                      <JournalEntryRow key={entry.journalEntryId} entry={entry} />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No journal entries found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Journal Entry Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>New Journal Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={newEntry.entryDate}
                onChange={(newValue) => handleEntryChange('entryDate', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </LocalizationProvider>
            
            <TextField
              label="Description"
              value={newEntry.description}
              onChange={(e) => handleEntryChange('description', e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Reference Number"
              value={newEntry.referenceNumber}
              onChange={(e) => handleEntryChange('referenceNumber', e.target.value)}
              fullWidth
            />
            
            <Typography variant="h6" sx={{ mt: 2 }}>Lines</Typography>
            
            {newEntry.lines.map((line, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Account</InputLabel>
                    <Select
                      value={line.accountId}
                      label="Account"
                      onChange={(e) => handleLineChange(index, 'accountId', e.target.value)}
                    >
                      {accounts.map((account) => (
                        <MenuItem key={account.accountId} value={account.accountId}>
                          {account.accountCode} - {account.accountName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Line Description"
                    value={line.description}
                    onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Debit"
                    type="number"
                    value={line.debit}
                    onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Credit"
                    type="number"
                    value={line.credit}
                    onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton onClick={() => removeLine(index)} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            
            <Button startIcon={<AddIcon />} onClick={addLine} sx={{ alignSelf: 'flex-start' }}>
              Add Line
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography>
                Total Debit: {newEntry.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0).toFixed(2)}
              </Typography>
              <Typography>
                Total Credit: {newEntry.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveEntry} 
            variant="contained" 
          >
            Save Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JournalEntries;
